import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FunctionsHttpError } from '@supabase/supabase-js';
import Recorder from 'recorder-core';
import { supabase } from '../supabaseClient';
import 'recorder-core/src/engine/wav.js';

import { 
  getPracticeStatus,
  updateInitialTestProgress as updateUserStatus,
  // getWeakestPhoneme, // 移除
  // generatePracticeWord, // 移除
  getNewPracticeSentence, // ✨ 新增
  postInitialTestResult as postPracticeSession,
  updateUserProgressSummary,
  getFullUserProfile, // ✨ 新增，用於獲取 API Key
  awardUserXp,
} from '../api/supabaseAPI';
import { getCorrectRateFromLog, getErrorRateFromLog, getSessionCreatedAt } from '../utils/practiceSession';

// Component Imports
import DiagnosisReport from '../components/DiagnosisReport';
import PracticeReportModal from '../components/PracticeReportModal';
import ConfirmDialog from '../components/ConfirmDialog';
import AudioRecorderControls from '../components/AudioRecorderControls';
import AlertOverlay from '../components/AlertOverlay';

// CSS Imports
import '../styles/Practice.css';
import '../styles/Layout.css';
import '../styles/Introduction.css';

// =================================================================
// ==   Practice Component (已修正命名錯誤)                      ==
// =================================================================
const useUser = (userId) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => getFullUserProfile(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 15, // API Key 不常變，可以設置較長的 staleTime
  });
};

export default function Practice({ practiceLanguage }) {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const { data: userData } = useUser(userId);

  // --- 本地 UI 狀態 ---
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [mediaError, setMediaError] = useState(null);
  const [isAcquiringMedia, setIsAcquiringMedia] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingDifficulty, setPendingDifficulty] = useState(null);
  const [isWaitingForGeneration, setIsWaitingForGeneration] = useState(false);
  const [isWaitingForAnalysis, setIsWaitingForAnalysis] = useState(false); // ✨ New state
  const [isAutoGenerationBlocked, setIsAutoGenerationBlocked] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  
  const recorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const queryKey = ['practiceStatus', userId, practiceLanguage];

  // --- Realtime Subscription for Background Generation ---
  useEffect(() => {
    if (!userId) return;

    let timeoutId;

    const channel = supabase
      .channel(`practice_realtime_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_status',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // Check if the update is for the current language
          if (payload.new.language === practiceLanguage) {
             // 1. Handle New Word Generation
             if (payload.new.cur_content) {
                 console.log("Realtime update: New Content:", payload.new.cur_content);
                 queryClient.invalidateQueries({ queryKey: queryKey });
                 setTimeout(() => setIsWaitingForGeneration(false), 500);
                 if (timeoutId) clearTimeout(timeoutId);
             }
             
             // 2. Handle Analysis Report Generation (✨ NEW)
             // We check if cur_report is NOT null (meaning it has been generated)
             if (payload.new.cur_report) {
                 console.log("Realtime update: New Report Generated");
                 queryClient.invalidateQueries({ queryKey: queryKey });
                 setTimeout(() => setIsWaitingForAnalysis(false), 500);
             }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_settings',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
           if (payload.new.error_status) {
              console.error("Realtime error status received:", payload.new.error_status);
              setIsWaitingForGeneration(false);
              if (timeoutId) clearTimeout(timeoutId);
              
              // Optionally clear the error status in DB so it doesn't persist, 
              // but we'll leave that to the user/next action for now.
           }
        }
      )
      .subscribe();

    // Safety timeout to prevent infinite loading state
    if (isWaitingForGeneration) {
      timeoutId = setTimeout(() => {
        if (isWaitingForGeneration) {
          console.warn("Generation timed out.");
          setIsWaitingForGeneration(false);
          alert("Generation timed out. Please try again.");
        }
      }, 60000); // 60 seconds timeout
    }

    return () => {
      supabase.removeChannel(channel);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [userId, practiceLanguage, queryClient, queryKey, isWaitingForGeneration]);


  // --- 統一的伺服器狀態數據源 ---
  const { data: practiceStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: queryKey,
    queryFn: () => getPracticeStatus(userId, practiceLanguage),
    enabled: !!userId && !!practiceLanguage,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  });

  // --- 衍生狀態 (Derived State) ---
  const currentWord = useMemo(() => practiceStatus?.cur_content, [practiceStatus]);
  const currentDifficulty = useMemo(() => practiceStatus?.cur_lvl || 'Primary-School', [practiceStatus]);
  const hasDiagnosisLog = useMemo(() => !!practiceStatus?.cur_log && typeof practiceStatus.cur_log === 'object', [practiceStatus]);
  const hasAnalysisReport = useMemo(() => !!practiceStatus?.cur_report && typeof practiceStatus.cur_report === 'object', [practiceStatus]);
  const hasValidAnalysisReport = useMemo(() => {
    return hasAnalysisReport && practiceStatus.cur_report.error === undefined;
  }, [hasAnalysisReport, practiceStatus]);
  const hasGlobalError = useMemo(() => !!userData?.settings?.error_status, [userData]);

  // --- Mutations ---

  // 1. 生成新單字
  const { mutate: triggerWordGeneration, isPending: isGeneratingWord } = useMutation({
    mutationFn: async (overrideDifficulty = null) => {
      // 1. Start waiting state
      setIsWaitingForGeneration(true);
      setIsAutoGenerationBlocked(false);

      const targetDifficulty = overrideDifficulty || currentDifficulty;
      
      // 2. Clear current content in DB to ensure Realtime triggers on new content
      // Also clear any previous logs/reports
      const updates = { 
        cur_report: null,
        cur_content: null, // Critical: Clear this so Realtime detects the change when new content arrives
        cur_log: null,
        cur_err: null
      };
      
      if (overrideDifficulty && overrideDifficulty !== currentDifficulty) {
          updates.cur_lvl = overrideDifficulty;
      }
      
      await updateUserStatus(userId, practiceLanguage, updates);
      
      const geminiApiKey = userData?.settings?.gemini_api_key;
      if (!geminiApiKey) {
        throw new Error("Gemini API Key not found in user settings. Please set it in your profile.");
      }

      // 3. Trigger the background generation task (returns 202 Accepted)
      await getNewPracticeSentence({
        language: practiceLanguage,
        difficulty: targetDifficulty,
        geminiApiKey: geminiApiKey,
      });
    },
    // Note: onSuccess is NOT used to stop loading, because 202 returns before generation is done.
    // Realtime subscription handles `setIsWaitingForGeneration(false)`.
    onError: (error) => {
      setIsWaitingForGeneration(false);
      setIsAutoGenerationBlocked(true);
      const errorMessage = error.context?.json?.()?.error || error.message || "An unknown error occurred.";
      alert(`Error generating new sentence: ${errorMessage}`);
    },
  });

  const { mutate: updateDifficulty, isPending: isUpdatingDifficulty } = useMutation({
    mutationFn: (newLevel) => updateUserStatus(userId, practiceLanguage, { cur_lvl: newLevel }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
    onError: (error) => console.error("Failed to update difficulty level:", error),
  });

  const { mutate: analyzeRecording, isPending: isAnalyzing } = useMutation({
    mutationFn: async (audioBlob) => {
      // 1. 【【【 將所有邏輯都移動到 mutationFn 中 】】】
      if (!audioBlob || !currentWord) throw new Error("Missing audio or word for analysis.");
      
      // 2. 調用 ASR 服務
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.wav');
      formData.append('target_content', currentWord);
      formData.append('language', practiceLanguage);
      const { data: asrData, error: asrError } = await supabase.functions.invoke('analyze-speech', { body: formData });
      if (asrError) {
        if (asrError instanceof FunctionsHttpError) {
          const errorJson = await asrError.context.json();
          throw new Error(`Analysis failed: ${errorJson.error || asrError.message}`);
        }
        throw asrError;
      }

      // 3. 在 mutationFn 內部處理數據聚合
      const phonemeStats = {};
      (asrData.words || [{ phonemes: asrData.phonemes || [] }])
        .flatMap(word => word.phonemes || [])
        .forEach(p => {
          if (p.target === '-') return;
          const targetPhoneme = p.target;
          if (!phonemeStats[targetPhoneme]) phonemeStats[targetPhoneme] = {};
          if (p.isMatch) {
            phonemeStats[targetPhoneme]['correct'] = (phonemeStats[targetPhoneme]['correct'] || 0) + 1;
          } else {
            const errorType = (p.user === '-') ? 'omission' : 'substitution';
            phonemeStats[targetPhoneme][errorType] = (phonemeStats[targetPhoneme][errorType] || 0) + 1;
          }
        });
      const statsToUpload = Object.entries(phonemeStats).flatMap(([phoneme, events]) => 
        Object.entries(events).map(([event_type, count]) => ({ phoneme, event_type, count }))
      );

      // 4. 在 mutationFn 內部，用一次 API 調用更新所有狀態
      await updateUserStatus(userId, practiceLanguage, { 
        cur_log: asrData,
        cur_err: statsToUpload
      });

      // 5. mutationFn 不需要返回任何東西，因為我們會用 invalidateQueries
    },
    onSuccess: () => {
      // 6. 【【【 簡化 onSuccess，回歸最可靠的模式 】】】
      // 在所有後台操作都完成後，我們只需要告訴前端：「數據變了，去重新獲取」。
      queryClient.invalidateQueries({ queryKey: queryKey });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
    onError: (error) => {
      alert(error.message);
      // 即使出錯，也刷新一下
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });

  const { mutate: analyzeReport, isPending: isGeneratingReport } = useMutation({
    mutationFn: async () => {
      // ✨ Start waiting state immediately
      setIsWaitingForAnalysis(true);
      
      const { data, error } = await supabase.functions.invoke('analyze-practice-session', {
        body: { language: practiceLanguage },
      });

      if (error) {
        if (error instanceof FunctionsHttpError) {
          const errorJson = await error.context.json();
          throw new Error(`Analysis report generation failed: ${errorJson.error || error.message}`);
        }
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      // Note: We DO NOT clear isWaitingForAnalysis here.
      // We wait for the Realtime subscription to do it when data arrives.
      queryClient.invalidateQueries({ queryKey: queryKey });
      console.log("Report generation initiated.");
    },
    onError: (error) => {
      // ✨ Stop waiting if API call fails
      setIsWaitingForAnalysis(false);
      queryClient.invalidateQueries({ queryKey: queryKey });
      alert(error.message);
    },
  });

  // 4. 推進到下一步
  const { mutate: advanceToNextStep, isPending: isAdvancing } = useMutation({
    mutationFn: async (reportData) => {
      // 1. 後台歸檔
      if (reportData.log) {
        const errorRate = getErrorRateFromLog(reportData.log, false);
        const correctRate = getCorrectRateFromLog(reportData.log, false);
        
        // ✨ Prepare created_at timestamp
        const createdAt = getSessionCreatedAt(reportData.log, false);

        if (reportData.err && reportData.err.length > 0) {
          await updateUserProgressSummary(userId, practiceLanguage, reportData.err);
        }
        await postPracticeSession({
          user_id: userId, 
          language: practiceLanguage, 
          target_content: reportData.content,
          diffi_level: reportData.difficulty,
          error_rate: errorRate, 
          full_log: reportData.log,
          practice_error: reportData.err || null,
          full_report: reportData.report || null,
          created_at: createdAt, // ✨ Include explicit timestamp
        });
        await awardUserXp(userId, 'practice_session', correctRate);
      }

      // 2. 後台清理和獲取新句子
      const nextSentence = reportData.report?.practice_sentence;
      if (nextSentence) {
        await updateUserStatus(userId, practiceLanguage, {
          cur_content: nextSentence,
          cur_log: null, cur_err: null, cur_report: null,
        });
      } else {
        // 如果沒有建議句，觸發後端生成新句子的流程
        const geminiApiKey = userData?.settings?.gemini_api_key;
        if (geminiApiKey) {
          await getNewPracticeSentence({
            language: practiceLanguage,
            difficulty: reportData.difficulty,
            geminiApiKey: geminiApiKey,
          });
        } else {
          // 作為備份，至少清理掉狀態
          await updateUserStatus(userId, practiceLanguage, {
            cur_content: null, // 讓 useEffect 去觸發生成
            cur_log: null, cur_err: null, cur_report: null,
          });
        }
      }
    },
    onSuccess: () => {
      // 3. 【【【 唯一的 onSuccess 邏輯 】】】
      queryClient.invalidateQueries({ queryKey: queryKey });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    },
    onError: (error) => {
      console.error("Failed to advance to next step:", error);
      alert("An error occurred while saving your session. Please try again.");
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });

  // --- Side Effects ---
  useEffect(() => {
    if (practiceStatus && !currentWord && !isGeneratingWord && !isWaitingForGeneration && !hasGlobalError && !isAutoGenerationBlocked) {
      triggerWordGeneration();
    }
  }, [practiceStatus, currentWord, isGeneratingWord, isWaitingForGeneration, hasGlobalError, isAutoGenerationBlocked, triggerWordGeneration]);

  useEffect(() => {
    let intervalId;
    if (isRecording) intervalId = setInterval(() => setTimer(prev => prev + 1), 1000);
    return () => clearInterval(intervalId);
  }, [isRecording]);

  const handleAnalysis = () => {
    if (hasAnalysisReport) {
      if (hasValidAnalysisReport) {
        // 情況 A: 報告有效，打開彈窗
        setIsReportModalOpen(true);
      } else {
        // 情況 B: 報告是個錯誤對象，提示用戶
        alert(practiceStatus.cur_report.error || "AI analysis failed. Please try again.");
        // 此處可以選擇自動觸發 handleTryAgain() 來重置
      }
    } else if (!isGeneratingReport) {
      // 情況 C: 還沒有報告，觸發生成
      analyzeReport();
    }
  };

  // --- Event Handlers ---
  const handleConfirm = () => {
    setIsConfirmOpen(false);
    if (pendingDifficulty) {
        // Change Difficulty AND Generate
        setIsAutoGenerationBlocked(false);
        triggerWordGeneration(pendingDifficulty);
    } else {
        // Just Try Another (Current Difficulty)
        setIsAutoGenerationBlocked(false);
        triggerWordGeneration(currentDifficulty);
    }
    setPendingDifficulty(null);
  };

  const handleDifficultyChange = (level) => {
    if (isUpdatingDifficulty || isGeneratingWord) return;
    setPendingDifficulty(level);
    setIsConfirmOpen(true);
  };

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  };

  const handleRecordToggle = async () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        (blob) => {
          setIsRecording(false);
          setTimer(0);
          analyzeRecording(blob);
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          setIsRecording(false);
          setMediaError(`Recording error: ${err}`);
          stopRecordingStream();
        }
      );
    } else {
      setMediaError(null);
      let selectedStream = null;
      if (selectedDeviceId) {
        try {
          selectedStream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: selectedDeviceId } },
          });
          recordingStreamRef.current = selectedStream;
        } catch (err) {
          setMediaError(err instanceof Error ? err.message : 'Microphone access failed.');
          return;
        }
      }

      const rec = Recorder({
        type: 'wav',
        sampleRate: 16000,
        bitRate: 16,
        sourceStream: selectedStream || undefined,
      });
      setIsAcquiringMedia(true);
      rec.open(
        () => {
          setIsAcquiringMedia(false);
          rec.start();
          recorderRef.current = rec;
          setIsRecording(true);
          setTimer(0);
        },
        (err, isUserNotAllow) => {
          setIsAcquiringMedia(false);
          setMediaError(isUserNotAllow ? "Microphone access was denied." : `Could not open recorder: ${err}`);
          stopRecordingStream();
        }
      );
    }
  };

  const handleDeleteRecording = () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        () => {
          setIsRecording(false);
          setTimer(0);
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          setIsRecording(false);
          setMediaError(`Recording error: ${err}`);
          stopRecordingStream();
        }
      );
    }
  };

  const handleTryAgain = () => {
    updateUserStatus(userId, practiceLanguage, { cur_log: null, cur_err: null, cur_report: null }).then(() => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    });
  };

  const handleTryAnother = () => {
    if (isGeneratingWord || hasDiagnosisLog) return;
    setPendingDifficulty(null);
    setIsConfirmOpen(true);
  };

  // --- Render Logic ---
  const formatTime = (seconds) => `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  const difficultyLevels = [
    { id: 'Kindergarten', label: 'kindergarten' },
    { id: 'PrimarySchool', label: 'primary_school' },
    { id: 'MiddleSchool', label: 'middle_school' },
    { id: 'HighSchool', label: 'high_school' },
    { id: 'University', label: 'university' }
  ];
  const isProcessing = isGeneratingWord || isAnalyzing || isAdvancing || isUpdatingDifficulty || isGeneratingReport || isWaitingForGeneration || isWaitingForAnalysis;

  if (isLoadingStatus) {
    return <main className="main-content width-practice practice-page-shell"><div className="spinner"></div></main>;
  }

  return (
    <>
      <main className="main-content width-practice practice-page-shell">
        <h1 className="page-title">{t('practicePage.title', 'Practice')}</h1>
        <div className="difficulty-section">
          <h3 className="section-title">{t('practicePage.difficultyLevel')}</h3>
          <div className="difficulty-selector">
            {difficultyLevels.map(level => (
              <button 
                key={level.id} 
                className={currentDifficulty === level.id ? 'active' : ''} 
                onClick={() => handleDifficultyChange(level.id)}
                disabled={isProcessing || hasDiagnosisLog}
              >
                {t(`practicePage.levels.${level.label}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="practice-area">
          <p className="practice-text">
            {(isGeneratingWord || isWaitingForGeneration) ? 'Generating word...' : (currentWord || '...')}
          </p>
          <div className="practice-controls">
            <button className="practice-btn" onClick={handleTryAgain} disabled={!hasDiagnosisLog || isProcessing}>
              {t('practicePage.tryAgain')}
            </button>
            <button 
              className="practice-btn primary" 
              onClick={handleTryAnother} 
              disabled={isProcessing || isRecording || hasDiagnosisLog}
            >
              {t('practicePage.tryAnother')}
            </button>
          </div>
        </div>

        {mediaError && <div className="media-error-alert">{mediaError}</div>}
        {isAcquiringMedia && <div className="media-info-alert">Please allow microphone access...</div>}

        {hasDiagnosisLog && (
          <DiagnosisReport 
            result={practiceStatus.cur_log}
            nextAction={
              <>
                <button 
                  className="practice-btn secondary"
                  onClick={handleAnalysis}
                  // ✨ 核心修改：添加一個條件，如果報告存在但無效，則禁用按鈕
                  disabled={isGeneratingReport || isWaitingForAnalysis || (hasAnalysisReport && !hasValidAnalysisReport)} 
                  style={{ marginBottom: '0.75rem' }}
                >
                  {(() => {
                    if (isGeneratingReport || isWaitingForAnalysis) {
                      return 'Analyzing ...';
                    }
                    if (hasValidAnalysisReport) {
                      return 'View Report';
                    }
                    if (hasAnalysisReport) {
                      return 'Analysis Failed'; 
                    }
                    return 'Analysis';
                  })()}
                </button>
                <button 
                  className="practice-btn primary" 
                  onClick={() => advanceToNextStep({
                    log: practiceStatus.cur_log,
                    err: practiceStatus.cur_err,
                    report: practiceStatus.cur_report,
                    content: practiceStatus.cur_content,
                    difficulty: practiceStatus.cur_lvl
                  })} 
                  disabled={isAdvancing || (hasDiagnosisLog && !hasValidAnalysisReport)} 
                >
                  {isAdvancing ? 'Saving...' : t('practicePage.next')}
                </button>
              </>
            }
          />
        )}

        {hasAnalysisReport && !hasValidAnalysisReport && (
          <div className="analysis-error-message">
            <p>
              AI analysis failed, likely because the speech was unclear or did not match the target sentence.
                

              Please click "Try Again" to record a new attempt.
            </p>
          </div>
        )}
      </main>

      <AudioRecorderControls
        isRecording={isRecording}
        recordDisabled={isProcessing || hasDiagnosisLog || isAcquiringMedia}
        deleteDisabled={!isRecording}
        timerText={formatTime(timer)}
        onRecordToggle={handleRecordToggle}
        onDelete={handleDeleteRecording}
        selectedDeviceId={selectedDeviceId}
        onDeviceChange={setSelectedDeviceId}
        micDisabled={isProcessing || hasDiagnosisLog || isAcquiringMedia || isRecording}
      />

      {(isGeneratingWord || isAnalyzing || isGeneratingReport || isWaitingForGeneration || isWaitingForAnalysis) && (
        <AlertOverlay
          message={
            (isGeneratingWord || isWaitingForGeneration)
              ? 'Generating ...'
              : (isGeneratingReport || isWaitingForAnalysis ? 'Analyzing Report ...' : t('practicePage.analyzing'))
          }
        />
      )}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={pendingDifficulty ? t('confirmDialog.changeDifficultyTitle') : t('confirmDialog.tryAnotherTitle')}
        message={pendingDifficulty 
          ? t('confirmDialog.changeDifficultyMessage') 
          : t('confirmDialog.tryAnotherMessage')}
        onConfirm={handleConfirm}
        onCancel={() => setIsConfirmOpen(false)}
      />
      <PracticeReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        report={practiceStatus?.cur_report}
      />
    </>
  );
}
